import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Search as SearchIcon, ScanLine, ShieldCheck, Gem, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VerifiedUnavailable } from "@/components/VerifiedUnavailable";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CoverageCTA } from "@/components/CoverageCTA";
import { getRecent } from "@/lib/recentListings";
import RecentlyViewed from "@/components/RecentlyViewed";
import ScoreBadge, { UnverifiedBadge } from "@/components/ScoreBadge";
import HeatStrip from "@/components/HeatStrip";
import { copyText } from "@/lib/clipboard";
import { groupStatesByRegion } from "@/lib/geo";
import { SearchSelect } from "@/components/SearchSelect";
import { FreshnessNote } from "@/components/FreshnessNote";

const FALLBACK_MAKES = ["Ferrari", "Lamborghini", "Rolls-Royce", "Bentley", "Bugatti", "Pagani", "Koenigsegg", "McLaren", "Aston Martin", "Rimac", "Czinger", "Maserati"];

export default function Vehicles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [makes, setMakes] = useState(FALLBACK_MAKES);
  const [models, setModels] = useState([]);
  const [geoStates, setGeoStates] = useState([]);
  const [vehHeat, setVehHeat] = useState(null);
  const [vehHeatLoading, setVehHeatLoading] = useState(false);
  const [filters, setFilters] = useState({ make: "", model: "", state: "", price_min: "150000", price_max: "", year_min: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [baseParams, setBaseParams] = useState(null);
  const [sort, setSort] = useState("price_desc");
  const [freshDays, setFreshDays] = useState(0);
  const PAGE = 24;
  const [vin, setVin] = useState("");
  const [vinResult, setVinResult] = useState(null);
  const [vinLoading, setVinLoading] = useState(false);
  const recentVehicles = getRecent("vehicle");
  const recentIds = new Set(recentVehicles.map((r) => r.id));
  const firstRecentId = recentVehicles[0]?.id;
  const recentRef = useRef(null);
  const [scores, setScores] = useState({});
  const [scoresLoaded, setScoresLoaded] = useState(false);
  const [bestValue, setBestValue] = useState(false);
  const [premium, setPremium] = useState(false);

  useEffect(() => { api.get("/billing/status").then((r) => setPremium(!!r.data.premium)).catch(() => {}); }, []);

  // Deep-link from the Coverage Map model movers: /vehicles?make=&model=&state= → prefill + search.
  useEffect(() => {
    const make = searchParams.get("make") || "";
    const model = searchParams.get("model") || "";
    const state = searchParams.get("state") || "";
    if (make || model || state) {
      const nf = { make, model, state, price_min: "150000", price_max: "", year_min: "" };
      setFilters(nf);
      runSearch(Object.fromEntries(Object.entries(nf).filter(([, v]) => v !== "")));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (recentRef.current) recentRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data]);

  useEffect(() => {
    if (data?.available && data.listings?.length) {
      setScoresLoaded(false);
      api.post("/valuations/batch", { kind: "vehicle", listings: data.listings })
        .then((r) => setScores(r.data.results || {}))
        .catch(() => setScores({}))
        .finally(() => setScoresLoaded(true));
    } else {
      setScores({});
      setScoresLoaded(false);
    }
  }, [data]);

  useEffect(() => {
    api.get("/vehicles/coverage").then((r) => setMakes(r.data.verified_makes?.length ? r.data.verified_makes : (r.data.tier1_makes || FALLBACK_MAKES))).catch(() => {});
  }, []);

  useEffect(() => { api.get("/config/geo", { params: { asset: "veh" } }).then((r) => setGeoStates(r.data.options || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!filters.make) { setModels([]); return; }
    api.get("/vehicles/models", { params: { make: filters.make } })
      .then((r) => setModels(r.data.models || []))
      .catch(() => setModels([]));
  }, [filters.make]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const setMake = (v) => setFilters((f) => ({ ...f, make: v, model: "" }));

  useEffect(() => {
    if (!filters.make && !filters.state) { setVehHeat(null); return; }
    setVehHeatLoading(true);
    api.get("/config/vehicle-heat", { params: { ...(filters.make ? { make: filters.make } : {}), ...(filters.state ? { state: filters.state } : {}) } })
      .then((r) => setVehHeat(r.data))
      .catch(() => setVehHeat(null))
      .finally(() => setVehHeatLoading(false));
  }, [filters.make, filters.state]);

  const selectHeatTile = (key) => {
    const nf = vehHeat?.level === "states" ? { ...filters, state: key } : { ...filters, make: key, model: "" };
    setFilters(nf);
    runSearch(Object.fromEntries(Object.entries(nf).filter(([, v]) => v !== "")));
  };

  const search = () => runSearch(Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "")));

  const runSearch = async (params, sortVal = sort, bv = bestValue) => {
    setBaseParams(params);
    setLoading(true);
    try {
      const res = await api.get("/vehicles", { params: { ...params, sort: sortVal, best_value: bv, limit: PAGE, start: 0 }, timeout: 45000 });
      setData(res.data);
    } catch { setData({ available: false, listings: [], total: 0 }); toast.error(bv ? "Best-value ranking took too long. Please try again or narrow your search." : "Search took too long. Please try again."); }
    finally { setLoading(false); }
  };

  const changeSort = (v) => { setSort(v); if (baseParams) runSearch(baseParams, v, bestValue); };
  const FRESH_DAY_MS = 86400000;
  const shownVeh = (data?.listings || []).filter((l) => !freshDays || (l._last_seen && (Date.now() - new Date(l._last_seen).getTime()) / FRESH_DAY_MS <= freshDays));
  const toggleBestValue = () => { const nb = !bestValue; setBestValue(nb); if (baseParams) runSearch(baseParams, sort, nb); };

  const loadMore = async () => {
    if (!baseParams) return;
    setLoadingMore(true);
    try {
      const res = await api.get("/vehicles", { params: { ...baseParams, sort, best_value: bestValue, limit: PAGE, start: data.listings.length }, timeout: 45000 });
      setData((d) => ({ ...res.data, listings: [...d.listings, ...res.data.listings] }));
    } catch {} finally { setLoadingMore(false); }
  };

  const decodeVin = async () => {
    if (!vin.trim()) return;
    setVinLoading(true); setVinResult(null);
    try {
      const res = await api.get(`/vehicles/decode-vin/${vin.trim()}`);
      setVinResult(res.data.specs);
    } catch { setVinResult({ error: true }); }
    finally { setVinLoading(false); }
  };

  const viewAll = () => {
    setFilters({ make: "", model: "", state: "", price_min: "150000", price_max: "", year_min: "" });
    runSearch({});
  };

  const VIN_KEYS = ["Make", "Model", "ModelYear", "Trim", "BodyClass", "EngineCylinders", "DisplacementL", "FuelTypePrimary", "DriveType", "TransmissionStyle", "PlantCountry", "Doors"];

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <Car className="w-6 h-6" strokeWidth={1.5} />
        <h1 className="font-display text-4xl tracking-tighter font-medium">Luxury Vehicles</h1>
      </div>
      <p className="text-muted-foreground mb-4">Verified dealer inventory via Auto.dev. Free VIN decoding via NHTSA.</p>
      <div className="flex items-start gap-2 bg-secondary/50 border border-border rounded-xl px-4 py-3 mb-8 max-w-3xl" data-testid="vehicle-scope-banner">
        <ShieldCheck className="w-4 h-4 text-accent mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">Ultra-luxury scope: <strong className="text-foreground">US-based</strong>, model year <strong className="text-foreground">2010+</strong>, asking price <strong className="text-foreground">$150,000+</strong>. Tier 1 marques (Ferrari, Lamborghini, Rolls-Royce, Bugatti, Pagani, Koenigsegg, McLaren…) show every qualifying model; Tier 2 marques (Porsche, Mercedes-Benz, BMW, Audi, Land Rover, Lexus, Tesla, Lucid) show flagship models only.</p>
      </div>

      <div className="bg-surface border border-border rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><ScanLine className="w-4 h-4" /><span className="font-display font-medium">VIN Decoder <span className="font-mono-data text-[10px] uppercase tracking-wider text-emerald-500 ml-2">Live</span></span></div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input data-testid="vin-input" value={vin} onChange={(e) => setVin(e.target.value)} placeholder="Enter 17-character VIN (e.g., ZFF79ALA4J0231234)" className="flex-1" />
          <Button onClick={decodeVin} disabled={vinLoading} data-testid="decode-vin-button" className="rounded-full px-8">{vinLoading ? "Decoding…" : "Decode VIN"}</Button>
        </div>
        {vinResult && (
          vinResult.error ? <div className="mt-4"><VerifiedUnavailable testId="vin-unavailable" note="This VIN could not be decoded by the NHTSA database." /></div> : (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="vin-result">
              {VIN_KEYS.filter((k) => vinResult[k]).map((k) => (
                <div key={k} className="metric-empty p-3">
                  <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/([A-Z])/g, " $1").trim()}</div>
                  <div className="font-mono-data text-sm mt-1">{vinResult[k]}</div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <div className="bg-surface border border-border rounded-xl p-6 grid grid-cols-2 md:grid-cols-7 gap-4 mb-10">
        <div className="col-span-2">
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Make</Label>
          <SearchSelect
            testid="filter-make"
            value={filters.make}
            onChange={(v) => setMake(v)}
            allOption={{ value: "", label: "Any" }}
            placeholder="Any"
            searchPlaceholder="Search make…"
            className="mt-1 w-full"
            options={makes.map((m) => ({ value: m }))}
          />
        </div>
        <div>
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Model</Label>
          <SearchSelect
            testid="filter-model"
            value={filters.model}
            onChange={(v) => set("model", v)}
            disabled={!filters.make || !models.length}
            allOption={{ value: "", label: "All models" }}
            placeholder={!filters.make ? "Select make first" : models.length ? "All models" : "No models"}
            searchPlaceholder="Search model…"
            className="mt-1 w-full"
            options={models.map((m) => ({ value: m }))}
          />
        </div>
        <div>
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">State</Label>
          <SearchSelect
            testid="filter-state"
            value={filters.state}
            onChange={(v) => set("state", v)}
            allOption={{ value: "", label: "All states" }}
            placeholder="All states"
            searchPlaceholder="Search state…"
            className="mt-1 w-full"
            groups={groupStatesByRegion(geoStates).map((g) => ({ label: g.region, options: g.options.map((o) => ({ value: o.value, count: o.count })) }))}
          />
        </div>
        <div><Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Min $</Label>
          <Input data-testid="filter-vprice-min" type="number" min="150000" step="25000" value={filters.price_min}
            onChange={(e) => set("price_min", e.target.value)}
            onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 150000) set("price_min", "150000"); }}
            className="mt-1" />
        </div>
        <div><Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Year ≥ (2010)</Label><Input data-testid="filter-year-min" type="number" min="2010" value={filters.year_min} onChange={(e) => set("year_min", e.target.value)} onBlur={(e) => { if (e.target.value && Number(e.target.value) < 2010) set("year_min", "2010"); }} placeholder="2010" className="mt-1" /></div>
        <div className="col-span-2 md:col-span-7 flex flex-wrap gap-3">
          <Button onClick={search} data-testid="search-vehicles-button" className="rounded-full px-8"><SearchIcon className="w-4 h-4 mr-2" />Search</Button>
          <Button onClick={viewAll} variant="outline" data-testid="view-all-vehicles-button" className="rounded-full"><Car className="w-4 h-4 mr-2" />View All Vehicles</Button>
        </div>
      </div>

      <HeatStrip
        tiles={vehHeat?.tiles || []}
        min={vehHeat?.min}
        max={vehHeat?.max}
        loading={vehHeatLoading}
        activeKey={vehHeat?.level === "states" ? filters.state : filters.make}
        onSelect={selectHeatTile}
        unit="listing"
        testidPrefix="veh-heat"
        title={vehHeat?.level === "states"
          ? `Median price by state${filters.make ? ` · ${filters.make}` : ""}`
          : `Median price by make${filters.state ? ` · ${filters.state}` : ""}`}
      />

      <RecentlyViewed kind="vehicle" items={recentVehicles} onSelect={(item) => navigate(`/vehicles/${item.id}`)} />

      {data && !loading && data.available && (data.listings.length > 0 || bestValue) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {premium ? (
            <button
              type="button"
              onClick={toggleBestValue}
              data-testid="best-value-filter"
              className={`font-mono-data text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${bestValue ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              <Gem className="w-3.5 h-3.5" strokeWidth={1.5} />Best value{bestValue ? ` · ${data.total}` : ""}
            </button>
          ) : <span />}
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Fresh</span>
              <SearchSelect
                testid="fresh-filter-vehicles"
                value={String(freshDays)}
                onChange={(v) => setFreshDays(Number(v))}
                align="end"
                options={[
                  { value: "0", label: "All listings" },
                  { value: "3", label: "Verified ≤ 3 days" },
                  { value: "7", label: "Verified ≤ 7 days" },
                  { value: "14", label: "Verified ≤ 14 days" },
                  { value: "30", label: "Verified ≤ 30 days" },
                ]}
              />
            </div>
            <div className="flex items-center">
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Sort</span>
              <SearchSelect
                testid="sort-vehicles"
                value={sort}
                onChange={changeSort}
                align="end"
                options={[
                  { value: "price_desc", label: "Price: High → Low" },
                  { value: "price_asc", label: "Price: Low → High" },
                  { value: "newest", label: "Newest" },
                  ...(premium ? [{ value: "score_desc", label: "Investment Score" }] : []),
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {loading && <div className="font-mono-data text-sm text-muted-foreground animate-pulse">Querying verified inventory…</div>}

      {data && !loading && (
        !data.available ? (
          <VerifiedUnavailable note="Connect the Auto.dev API to display verified luxury vehicle inventory and dealer information." />
        ) : data.listings.length === 0 ? (
          bestValue
            ? <VerifiedUnavailable testId="no-best-value" note="No vehicles in this search have a verified Strong-Value score. Turn off ‘Best value’ to see all listings." />
            : <VerifiedUnavailable note="No verified vehicles matched your search. Try broader filters." />
        ) : shownVeh.length === 0 ? (
          <VerifiedUnavailable testId="no-fresh-vehicles" note={`No vehicles on this page were verified within the last ${freshDays} days. Widen the freshness filter to see more.`} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shownVeh.map((l, i) => (
              <motion.div key={l.id || i} ref={l.id === firstRecentId ? recentRef : undefined} whileHover={{ y: -6 }} onClick={() => navigate(`/vehicles/${l.id}`)}
                className={`bg-surface border rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg ${recentIds.has(l.id) ? "border-accent ring-2 ring-accent/40" : "border-border"}`} data-testid="vehicle-card">
                <div className="relative">
                  {l.photos?.[0] ? <img src={l.photos[0]} alt={l.heading} className="w-full h-52 object-cover" /> : <div className="w-full h-52 metric-empty flex items-center justify-center font-mono-data text-xs text-muted-foreground">No verified photo</div>}
                  {scores[l.id] ? <ScoreBadge data={scores[l.id]} className="absolute top-3 right-3 backdrop-blur-sm" /> : scoresLoaded ? <UnverifiedBadge className="absolute top-3 right-3 backdrop-blur-sm" /> : null}
                </div>
                <div className="p-5">
                  <div className="font-mono-data text-xl">{l.price ? `$${Number(l.price).toLocaleString()}` : "—"}</div>
                  <div className="font-editorial text-xl italic mt-1">{l.heading || `${l.year || ""} ${l.make || ""} ${l.model || ""}`.trim() || "—"}</div>
                  <div className="flex items-center gap-4 mt-3 font-mono-data text-sm text-muted-foreground">
                    <span>{l.miles ? `${Number(l.miles).toLocaleString()} mi` : "—"}</span><span>{l.transmission || "—"}</span>
                  </div>
                  <FreshnessNote lastSeen={l._last_seen} agingOnly className="mt-2" />
                  {l.vin && (
                    <div className="flex items-center justify-between gap-2 mt-4">
                      <div className="font-mono-data text-[10px] text-muted-foreground/70 uppercase tracking-wider truncate">VIN {l.vin}</div>
                      <button
                        data-testid="card-copy-vin"
                        title="Copy VIN"
                        onClick={async (e) => { e.stopPropagation(); (await copyText(l.vin)) ? toast.success("VIN copied") : toast.error("Could not copy"); }}
                        className="shrink-0 text-muted-foreground hover:text-accent transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )
      )}
      {data && !loading && data.available && data.listings.length > 0 && (
        <div className="flex flex-col items-center gap-3 mt-10">
          <div className="font-mono-data text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="vehicles-count">Showing {shownVeh.length} of {data.total} verified vehicles</div>
          {data.listings.length < data.total && (
            <Button onClick={loadMore} disabled={loadingMore} variant="outline" data-testid="load-more-vehicles-button" className="rounded-full px-8">{loadingMore ? "Loading…" : "Load more"}</Button>
          )}
        </div>
      )}
      {data && !loading && data.available && data.listings.length > 0 && <CoverageCTA kind="vehicles" />}
      {!data && !loading && <VerifiedUnavailable testId="vehicles-initial-empty" note="Search above, or decode any VIN instantly using the live NHTSA decoder." />}
    </div>
  );
}
